/**
 * Action Parser — extracts fenced action JSON blocks from LLM responses.
 *
 * The LLM is instructed to emit action blocks like:
 *
 * ```action
 * { "type": "create_chart", "dataRange": "A1:D10", "chartType": "bar" }
 * ```
 *
 * This parser finds all such blocks and returns them as typed actions.
 */

import type {ActionResult} from "./actionExecutor";
import {actionRegistry} from "./actionRegistry";

export type ActionType =
  | "create_chart"
  | "format_chart"
  | "write_formula"
  | "write_data"
  | "sort_data"
  | "filter_data"
  | "format_cells"
  | "toggle_gridlines"
  | "create_sheet"
  | "run_script";

export interface CreateChartAction {
  type: "create_chart";
  chartType: string;
  dataRange: string;
  title?: string;
  /**
   * Where to place the chart on the sheet, chosen by the LLM so charts don't
   * stack on top of each other. Either a range ("G1:O15") or a single
   * top-left anchor cell ("G1").
   */
  position?: string;
  /** Named palette, chosen deliberately by the LLM instead of Excel's flat default. */
  colorScheme?: string;
  /** Show the value on each bar/slice/point. */
  dataLabels?: boolean;
  legend?: {
    position?: string;
    visible?: boolean;
  };
  axisTitles?: {
    x?: string;
    y?: string;
  };
}

export interface FormatChartAction {
  type: "format_chart";
  /** Name of an existing chart, e.g. "Chart 1" (as shown in the sheet layout markers). */
  chartName: string;
  sheetName?: string;
  title?: string;
  colorScheme?: string;
  dataLabels?: boolean;
  legend?: {
    position?: string;
    visible?: boolean;
  };
  axisTitles?: {
    x?: string;
    y?: string;
  };
}

export interface WriteFormulaAction {
  type: "write_formula";
  cell: string;
  formula: string;
}

export interface WriteDataAction {
  type: "write_data";
  startCell: string;
  values: (string | number | boolean)[][];
}

export interface SortDataAction {
  type: "sort_data";
  range: string;
  column: number;
  ascending: boolean;
}

export interface FilterDataAction {
  type: "filter_data";
  range: string;
  column: number;
  criteria: string;
}

export interface FormatCellsAction {
  type: "format_cells";
  range: string;
  fillColor?: string;
  fontColor?: string;
  bold?: boolean;
  italic?: boolean;
  fontSize?: number;
  /** Excel number format code, e.g. "#,##0.00", "dd/mm/yyyy", "0%". */
  numberFormat?: string;
}

export interface ToggleGridlinesAction {
  type: "toggle_gridlines";
  show: boolean;
}

export interface CreateSheetAction {
  type: "create_sheet";
  name: string;
}

export interface RunScriptAction {
  type: "run_script";
  /**
   * The BODY of an async function that receives `context` (an
   * Excel.RequestContext already inside an active Excel.run batch). May
   * `return` a JSON-serialisable value, which is reported back to the model.
   */
  code: string;
  /** Short human-readable summary shown to the user instead of the code. */
  description: string;
  /**
   * Plain-English question the user must confirm before the script runs.
   * Required only when the script is destructive (deletes, clears, or
   * overwrites a populated range or sheet); omitted otherwise.
   */
  confirmMessage?: string;
}

export type SpreadsheetAction =
  | CreateChartAction
  | FormatChartAction
  | WriteFormulaAction
  | WriteDataAction
  | SortDataAction
  | FilterDataAction
  | FormatCellsAction
  | ToggleGridlinesAction
  | CreateSheetAction
  | RunScriptAction;

export interface ParseResult {
  /** The response text with action blocks removed (for display). */
  displayText: string;
  /** Extracted action blocks that passed registry validation. */
  actions: SpreadsheetAction[];
  /** Failure results for blocks with an unknown type or a validation failure. */
  invalidActionResults: ActionResult[];
  /** Raw JSON of every well-formed action block, kept for an optional "show instructions" view. */
  rawActionBlocks?: string[];
  /** Instrumentation-only failures for malformed blocks that remain visible. */
  invalidActionTelemetry?: InvalidActionTelemetry[];
}

export type InvalidActionReason =
  | "malformed_json"
  | "not_an_object"
  | "unknown_type"
  | "validation_failed";

export interface InvalidActionTelemetry {
  reason: InvalidActionReason;
  /** The model-provided type, when present; never use it as a metric label. */
  actionType?: string;
}

const ACTION_BLOCK_REGEX = /```action\s*\n([\s\S]*?)```/g;

/** The opening fence the model wraps every action block in. */
const ACTION_FENCE = "```action";

/**
 * The shortest tail we treat as a fence still arriving ("```a"), so a lone
 * "```" — which just as likely opens or closes a normal code block — is
 * never swallowed.
 */
const MIN_PARTIAL_FENCE = 4;

const KNOWN_ACTION_TYPES = Object.keys(
  actionRegistry,
) as SpreadsheetAction["type"][];

export function parseActions(responseText: string): ParseResult {
  const actions: SpreadsheetAction[] = [];
  const invalidActionResults: ActionResult[] = [];
  const rawActionBlocks: string[] = [];
  const invalidActionTelemetry: InvalidActionTelemetry[] = [];
  let displayText = responseText;

  const matches = [...responseText.matchAll(ACTION_BLOCK_REGEX)];

  for (const match of matches) {
    const jsonStr = match[1].trim();
    try {
      const parsed = JSON.parse(jsonStr);
      // Support a single action object or an array of actions
      const items = Array.isArray(parsed) ? parsed : [parsed];
      for (const item of items) {
        const evaluated = evaluateAction(item);
        if (evaluated.ok) {
          actions.push(evaluated.action);
        } else {
          invalidActionResults.push(evaluated.result);
          invalidActionTelemetry.push(evaluated.telemetry);
        }
      }
      rawActionBlocks.push(jsonStr);
    } catch (err) {
      // Malformed JSON remains visible, must not disappear from telemetry,
      // and — like every other failure — must go back to the model so the
      // loop can correct it. The parse error's own message ("Bad control
      // character in string literal…") is the most specific hint we have,
      // so lead with it; the most common cause is a raw newline inside a
      // string literal, which JSON requires to be escaped as \n.
      const reason = err instanceof Error ? err.message : String(err);
      invalidActionResults.push({
        outcome: "failure",
        retryable: true,
        message: `Action failed: the action block is malformed JSON and could not be parsed (${reason}). Re-emit it as strict JSON: every string value must stay on one line — escape newlines as \\n — and escape double quotes inside strings as \\".`,
      });
      invalidActionTelemetry.push({reason: "malformed_json"});
      continue;
    }
    // Remove the action block from display text
    displayText = displayText.replace(match[0], "").trim();
  }

  return {
    displayText,
    actions,
    invalidActionResults,
    rawActionBlocks,
    invalidActionTelemetry,
  };
}

export interface StreamingSplit {
  /** The prose worth showing while the rest of the response is still coming. */
  visibleText: string;
  /** Action blocks seen so far, counting one that is still being written. */
  actionBlockCount: number;
}

/**
 * The streaming counterpart to `parseActions`: strips action blocks out of a
 * half-arrived response so the user never watches raw JSON being typed out.
 *
 * `parseActions` can only work on a finished response — its regex needs the
 * closing fence — but the bubble is painted on every chunk. This function
 * therefore also treats an unterminated block, and even a fence still being
 * spelled out ("```acti"), as an action block, so the JSON is hidden from the
 * first keystroke rather than after the last one.
 */
export function splitStreamingText(text: string): StreamingSplit {
  let visibleText = "";
  let actionBlockCount = 0;
  let cursor = 0;

  for (;;) {
    const open = text.indexOf(ACTION_FENCE, cursor);
    if (open === -1) break;
    visibleText += text.slice(cursor, open);
    actionBlockCount++;
    const close = text.indexOf("```", open + ACTION_FENCE.length);
    if (close === -1) {
      // Still being written — everything from the fence on is action JSON.
      return {visibleText: tidy(visibleText), actionBlockCount};
    }
    cursor = close + 3;
  }

  const tail = trimPartialFence(text.slice(cursor));
  if (tail.opened) actionBlockCount++;
  return {visibleText: tidy(visibleText + tail.text), actionBlockCount};
}

/**
 * Drops a trailing fence that has only partly arrived, so the bubble doesn't
 * flash "```act" for a frame before the block is recognised.
 */
function trimPartialFence(text: string): {text: string; opened: boolean} {
  for (let n = ACTION_FENCE.length - 1; n >= MIN_PARTIAL_FENCE; n--) {
    if (text.endsWith(ACTION_FENCE.slice(0, n))) {
      return {text: text.slice(0, -n), opened: true};
    }
  }
  return {text, opened: false};
}

/** Closes the gap a removed block leaves behind. */
function tidy(text: string): string {
  return text.replace(/\n{3,}/g, "\n\n").trimEnd();
}

type ActionEvaluation =
  | {ok: true; action: SpreadsheetAction}
  | {ok: false; result: ActionResult; telemetry: InvalidActionTelemetry};

function evaluateAction(item: unknown): ActionEvaluation {
  if (typeof item !== "object" || item === null) {
    return {
      ok: false,
      telemetry: {reason: "not_an_object"},
      result: {
        outcome: "failure",
        retryable: true,
        message: `Action failed: expected an action object, got ${JSON.stringify(item)}.`,
      },
    };
  }

  const candidate = item as Record<string, unknown>;
  const type = candidate.type;
  const definition = actionRegistry[type as SpreadsheetAction["type"]];

  if (!definition) {
    return {
      ok: false,
      telemetry: {
        reason: "unknown_type",
        actionType: typeof type === "string" ? type : undefined,
      },
      result: {
        outcome: "failure",
        message: `Unknown action type "${String(type)}" — this capability is not supported. Do not retry it; tell the user plainly that the add-in can't do this yet. Supported action types: ${KNOWN_ACTION_TYPES.join(", ")}.`,
      },
    };
  }

  if (!definition.validate(candidate)) {
    return {
      ok: false,
      telemetry: {
        reason: "validation_failed",
        actionType: typeof type === "string" ? type : undefined,
      },
      result: {
        outcome: "failure",
        retryable: true,
        message: `Invalid parameters for action type "${type}". Expected shape:\n\n${definition.promptDoc}`,
      },
    };
  }

  return {ok: true, action: candidate as unknown as SpreadsheetAction};
}
